import { z } from 'zod';
/**
 * ehr#292 - core communication consent (OWNERSHIP §4.7): one channel-generic set of consent
 * categories, per-contact preferences and suppressions that every sender reads (Outreach first).
 *
 *   category    a purpose a contact can opt in or out of; `transactional` purposes (appointments,
 *               billing, care) skip preferences, `marketing` ones honor them.
 *   preference  one contact's answer for one category on one channel. No row = never answered.
 *   suppression a channel address that must not be used at all (bounce, complaint, STOP,
 *               unsubscribe, manual) - transactional sends included.
 *
 * The contact's per-channel opt-in (contacts.communicationConsent, "honor real opt-in") stays the
 * channel switch: an unanswered marketing preference falls back to it.
 */
export const CONSENT_KINDS = ['transactional', 'marketing'];
export const COMMS_CHANNELS = ['email', 'sms', 'voice'];
/** reminders#4: every channel a consent answer can be on: the three message channels plus
 *  AI ambient listening in sessions (an answer on the care category, never a send). */
export const PREFERENCE_CHANNELS = [...COMMS_CHANNELS, 'ambient'];
export const SUPPRESSION_REASONS = [
    'manual',
    'hard_bounce',
    'soft_bounce_limit',
    'complaint',
    'unsubscribe',
    'stop',
];
/** The categories every org starts with (the Outreach seed), in order. Keys are stable. */
export const DEFAULT_CONSENT_CATEGORIES = [
    {
        key: 'appointments',
        label: 'Appointment reminders',
        kind: 'transactional',
        description: 'Visit confirmations, reminders, and reschedules.',
    },
    {
        key: 'billing',
        label: 'Billing & statements',
        kind: 'transactional',
        description: 'Invoices, balances, and payment receipts.',
    },
    {
        key: 'care',
        label: 'Care & clinical updates',
        kind: 'transactional',
        description: 'Treatment plan, results, and care-team messages.',
    },
    {
        key: 'marketing',
        label: 'Newsletters & marketing',
        kind: 'marketing',
        description: 'Program news, tips, and promotions.',
    },
    {
        key: 'surveys',
        label: 'Surveys & feedback',
        kind: 'marketing',
        description: 'Satisfaction surveys and feedback requests.',
    },
    {
        key: 'events',
        label: 'Events & workshops',
        kind: 'marketing',
        description: 'Invitations to groups, classes, and events.',
    },
];
// Ids minted by a client (Outreach mints with crypto.randomUUID()); the server mints otherwise.
const uuidV4 = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i, 'uuid v4');
const source = z.string().trim().min(1).max(40);
export const consentCategoryCreateSchema = z.object({
    id: uuidV4.optional(),
    /** Defaults to a slug of the label. */
    key: z
        .string()
        .regex(/^[a-z][a-z0-9_]{1,39}$/)
        .optional(),
    label: z.string().trim().min(1).max(80),
    kind: z.enum(CONSENT_KINDS),
    description: z.string().trim().max(300).default(''),
});
export const consentCategoryUpdateSchema = z
    .object({
    label: z.string().trim().min(1).max(80).optional(),
    kind: z.enum(CONSENT_KINDS).optional(),
    description: z.string().trim().max(300).optional(),
    position: z.number().int().min(0).max(1000).optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');
/** PUT a contact's answers: { categoryId: optedIn } for one channel. */
export const consentPreferencesSchema = z.object({
    channel: z.enum(COMMS_CHANNELS).default('email'),
    prefs: z
        .record(z.string().min(1).max(64), z.boolean())
        .refine((o) => Object.keys(o).length > 0 && Object.keys(o).length <= 50, '1 to 50 categories'),
    source: source.default('staff'),
});
/** Opt a contact out of every marketing category on one channel. */
export const consentUnsubscribeSchema = z.object({
    channel: z.enum(COMMS_CHANNELS).default('email'),
    source: source.default('unsubscribe'),
});
export const suppressionCreateSchema = z.object({
    id: uuidV4.optional(),
    channel: z.enum(COMMS_CHANNELS),
    address: z.string().trim().min(3).max(254),
    reason: z.enum(SUPPRESSION_REASONS).default('manual'),
    note: z.string().trim().max(500).default(''),
    contactId: z.string().min(1).max(64).optional(),
    source: source.default('staff'),
});
/**
 * The form an address is stored and matched in: an email lowercased and trimmed; a phone as
 * digits only, with a 10-digit US number given its leading 1. Null when it isn't usable.
 */
export function normalizeConsentAddress(channel, address) {
    const a = address.trim();
    if (channel === 'email') {
        const e = a.toLowerCase();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : null;
    }
    let d = a.replace(/\D/g, '');
    if (d.length === 10)
        d = `1${d}`;
    return d.length >= 11 && d.length <= 15 ? d : null;
}
/** A slug key for a category label ("Events & workshops" -> "events_workshops"). */
export function consentKeyFor(label) {
    const s = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
    return /^[a-z]/.test(s) && s.length >= 2 ? s : `cat_${s}`.slice(0, 40);
}
/**
 * May this category be sent to this contact on this channel? Suppression always wins.
 * Transactional (care) sends go out unless the client said no (reminders "decision 17", Jay
 * 10-05: never asked = okay, No = a dated opt-out; reminders#4 lets staff record that No).
 * Marketing uses the contact's answer, or when unanswered their channel opt-in
 * (contacts.communicationConsent: explicit opt-in only).
 */
export function effectiveConsent(c) {
    if (c.suppressed)
        return false;
    if (c.kind === 'transactional')
        return c.pref !== false;
    return c.pref ?? c.channelOptIn;
}
// ---------------------------------------------------------------------------------------------
// reminders#4 - the Opt-Out Center (Chase's OPT_OUT_CENTER_10-05-V1; Jay 10-08: in the EHR, on
// core consent). One switch per purpose and channel: care vs marketing per channel (his 5b),
// plus AI ambient listening chart-wide (11c). A switch writes every category of its kind on
// its channel; it reads "some" when an older per-category answer (Outreach's email
// preferences) differs.
export const OPT_OUT_COLUMNS = [
    { key: 'care_sms', label: 'Care texts', kind: 'transactional', channel: 'sms' },
    { key: 'care_email', label: 'Care email', kind: 'transactional', channel: 'email' },
    { key: 'care_voice', label: 'Care calls', kind: 'transactional', channel: 'voice' },
    { key: 'mkt_sms', label: 'Marketing texts', kind: 'marketing', channel: 'sms' },
    { key: 'mkt_email', label: 'Marketing email', kind: 'marketing', channel: 'email' },
    { key: 'ai', label: 'AI listening', kind: 'transactional', channel: 'ambient' },
];
export const OPT_OUT_COLUMN_KEYS = OPT_OUT_COLUMNS.map((c) => c.key);
export const optOutColumn = (key) => OPT_OUT_COLUMNS.find((c) => c.key === key);
/** The categories one switch writes: AI listening is the care category only. */
export function optOutCategories(key, categories) {
    if (key === 'ai')
        return categories.filter((c) => c.key === 'care');
    const col = optOutColumn(key);
    return categories.filter((c) => c.kind === col.kind);
}
/**
 * One client's switch. Suppression wins (STOP on texts reads "stop"); otherwise each category
 * of the switch is on or off by the send rule (effectiveConsent), with decision 17's plain
 * contacts.okText / okEmail / okCall standing in for an unanswered appointments category.
 */
export function optOutCell(input) {
    const col = optOutColumn(input.key);
    const cats = optOutCategories(input.key, input.categories);
    const mine = input.prefs.filter((p) => p.channel === col.channel && cats.some((c) => c.id === p.categoryId));
    const latest = mine
        .slice()
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
    const base = {
        updatedAt: latest?.updatedAt ?? null,
        reason: latest?.reason ?? '',
        source: latest?.source ?? '',
    };
    const sup = col.channel === 'ambient' ? null : (input.suppressed[col.channel] ?? null);
    if (sup)
        return { ...base, state: sup === 'stop' ? 'stop' : 'off', suppression: sup };
    const values = cats.map((c) => {
        const p = mine.find((x) => x.categoryId === c.id);
        let pref = p ? p.optedIn : null;
        if (pref === null && c.key === 'appointments' && col.channel !== 'ambient')
            pref = input.legacy?.[col.channel] ?? null;
        return effectiveConsent({
            kind: c.kind,
            suppressed: false,
            pref,
            channelOptIn: col.channel === 'ambient' ? false : (input.channelOptIn[col.channel] ?? false),
        });
    });
    const state = !values.length
        ? 'off'
        : values.every(Boolean)
            ? 'on'
            : values.some(Boolean)
                ? 'some'
                : 'off';
    return { ...base, state, suppression: null };
}
/** Turn one switch on or off for a client. Turning it back on records the client's documented
 *  consent (Chase's 7c: staff re-enroll with documented consent); turning off may say why. */
export const optOutSetSchema = z
    .object({
    column: z.enum(OPT_OUT_COLUMN_KEYS),
    on: z.boolean(),
    reason: z.string().trim().max(300).default(''),
    source: source.default('staff'),
})
    .refine((v) => !v.on || v.reason.length >= 3, {
    message: 'Say how the client agreed (their documented consent)',
    path: ['reason'],
});
/** A module (Reminders) records a client's own unsubscribe or re-subscribe. */
export const consentMachineOptOutSchema = z.object({
    column: z.enum(OPT_OUT_COLUMN_KEYS),
    on: z.boolean(),
    reason: z.string().trim().max(300).default(''),
    source: source,
});
/** A client texted STOP (all texts off) or START (texts back on) to a module's number. */
export const consentMachineStopSchema = z.object({
    stop: z.boolean(),
    /** The number they texted from; defaults to the contact's mobile, then phone. */
    address: z.string().trim().min(7).max(40).optional(),
    source: source,
});
//# sourceMappingURL=comms-consent.js.map